import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Check, X, Edit } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TransactionForm } from "./TransactionComponents";

export default function AssistantRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [requests, setRequests] = useState([]);
  const [tags, setTags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Form states
  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [editTxId, setEditTxId] = useState(null);
  const [type, setType] = useState("expense");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("TL");
  const [accountId, setAccountId] = useState("manual");
  const [accountName, setAccountName] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("none");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  // Sub-modal states
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [accDialogOpen, setAccDialogOpen] = useState(false);
  const [manualAccName, setManualAccName] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, tagRes, accRes, pmRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/requests`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tags`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/payment_methods`, { withCredentials: true })
      ]);
      setRequests(reqRes.data);
      setTags(tagRes.data);
      setAccounts(accRes.data);
      setPaymentMethods(pmRes.data);
    } catch (e) {
      toast.error("İstekler alınamadı.");
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [fetchData, isAdmin]);

  const handleApprove = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/requests/${id}/approve`, {}, { withCredentials: true });
      toast.success("İşlem onaylandı ve ana tabloya eklendi.");
      fetchData();
    } catch (e) {
      toast.error("İşlem onaylanamadı.");
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/requests/${id}/reject`, {}, { withCredentials: true });
      toast.success("İşlem reddedildi ve silindi.");
      fetchData();
    } catch (e) {
      toast.error("İşlem reddedilemedi.");
    }
  };

  const handleEditClick = (tx) => {
    setEditTxId(tx.id);
    setType(tx.type);
    setDate(new Date(tx.date).toISOString().split('T')[0]);
    setAmount(tx.amount.toString());
    setCurrency(tx.currency || "TL");
    if (tx.account_id) {
      setAccountId(tx.account_id);
      setAccountName(tx.account_name || "");
      setManualAccName("");
    } else if (tx.account_name) {
      setAccountId("manual");
      setAccountName(tx.account_name);
      setManualAccName(tx.account_name);
    } else {
      setAccountId("manual");
      setAccountName("");
      setManualAccName("");
    }
    setPaymentMethodId(tx.payment_method_id || "none");
    setDescription(tx.description || "");
    setSelectedTags(tx.tags || []);
    setTxDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!amount) return;
    if (accountId === "manual" && !accountName.trim()) {
      toast.error("Lütfen bir cari hesap seçin veya elle bir isim girin.");
      return;
    }

    let finalAccId = accountId === "manual" ? null : accountId;
    let finalAccName = accountId === "manual" ? accountName : accounts.find(a => a.id === accountId)?.name;
    let finalPmId = paymentMethodId === "none" ? null : paymentMethodId;
    let finalPmName = paymentMethodId === "none" ? null : paymentMethods.find(p => p.id === paymentMethodId)?.name;

    try {
      await axios.put(`${BACKEND_URL}/api/transactions/${editTxId}`, {
        type,
        date: new Date(date).toISOString(),
        amount: parseFloat(amount),
        currency,
        account_id: finalAccId,
        account_name: finalAccName,
        payment_method_id: finalPmId,
        payment_method_name: finalPmName,
        description,
        tags: selectedTags
      }, { withCredentials: true });
      
      toast.success("İstek başarıyla düzenlendi.");
      setTxDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error("İstek güncellenemedi.");
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]);
  };

  const handleAddNewTag = async () => {
    if (!newTag.trim()) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/tags`, { name: newTag.trim(), color: "#0A0A0A" }, { withCredentials: true });
      toast.success("Yeni etiket oluşturuldu.");
      setTags([...tags, res.data]);
      setSelectedTags([...selectedTags, res.data.id]);
      setNewTag("");
    } catch (e) {
      toast.error("Etiket oluşturulamadı.");
    }
  };

  if (!isAdmin) {
    return <div className="p-8 text-center text-muted-foreground">Bu sayfayı görüntüleme yetkiniz yok.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between border-b border-border pb-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Yardımcı İstekleri</h1>
          <p className="text-sm text-muted-foreground mt-1">Yardımcı yöneticilerin girdikleri gider masraflarını onaylayıp ana tabloya aktarın.</p>
        </div>
      </div>

      <Dialog open={txDialogOpen} onOpenChange={setTxDialogOpen}>
        <DialogContent className="sm:max-w-[750px] p-0 overflow-hidden gap-0 rounded-sm">
          <DialogHeader className="p-6 border-b border-border bg-white">
            <DialogTitle className="font-heading tracking-tight text-xl">İşlemi Düzenle</DialogTitle>
          </DialogHeader>
          <div className="p-6 md:p-8 max-h-[75vh] overflow-y-auto bg-background">
            <TransactionForm 
              handleCreate={handleUpdate} type={type} setType={setType} date={date} setDate={setDate}
              amount={amount} setAmount={setAmount} currency={currency} setCurrency={setCurrency}
              accountId={accountId} setAccountId={setAccountId} accountName={accountName} setAccountName={setAccountName}
              paymentMethodId={paymentMethodId} setPaymentMethodId={setPaymentMethodId} paymentMethods={paymentMethods}
              description={description} setDescription={setDescription} accounts={accounts} 
              accDialogOpen={accDialogOpen} setAccDialogOpen={setAccDialogOpen} manualAccName={manualAccName}
              setManualAccName={setManualAccName} selectedTags={selectedTags} tags={tags} toggleTag={toggleTag}
              tagDialogOpen={tagDialogOpen} setTagDialogOpen={setTagDialogOpen} newTag={newTag} 
              setNewTag={setNewTag} handleAddNewTag={handleAddNewTag} handleUpload={() => {}}
              isEditMode={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-border rounded-sm overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="bg-background text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground border-b border-border">
              <tr>
                <th className="px-5 py-4">Tarih</th>
                <th className="px-5 py-4">Yardımcı (Giren Kişi)</th>
                <th className="px-5 py-4">Hesap / Kişi</th>
                <th className="px-5 py-4">Açıklama</th>
                <th className="px-5 py-4">Etiketler</th>
                <th className="px-5 py-4 text-right">Tutar</th>
                <th className="px-5 py-4 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">Onay bekleyen işlem bulunamadı.</td></tr>
              ) : (
                requests.map(tx => (
                  <tr key={tx.id} className="border-b border-border last:border-0 hover:bg-neutral-50/50 transition-colors">
                    <td className="px-5 py-4">{new Date(tx.date).toLocaleDateString('tr-TR')}</td>
                    <td className="px-5 py-4 font-medium text-primary">
                      {tx.created_by_name || "Bilinmeyen"}
                    </td>
                    <td className="px-5 py-4 font-medium">
                      {tx.account_name || "-"}
                    </td>
                    <td className="px-5 py-4 text-muted-foreground max-w-[200px] group relative">
                      <div className="truncate group-hover:whitespace-normal group-hover:break-words">{tx.description}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1.5 flex-wrap">
                        {tx.tags.map(tid => {
                          const tag = tags.find(t => t.id === tid);
                          return tag ? (
                            <span key={tid} className="px-2 py-0.5 rounded-sm text-[10px] font-medium text-white shadow-sm" style={{ backgroundColor: tag.color }}>
                              {tag.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right font-medium font-heading tracking-tight text-base text-expense">
                      {tx.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {tx.currency}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="outline" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10" onClick={() => handleEditClick(tx)} data-testid={`edit-req-${tx.id}`}>
                          <Edit size={14} />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 bg-green-50 text-green-700 hover:bg-green-100 border-green-200" onClick={() => handleApprove(tx.id)} data-testid={`approve-tx-${tx.id}`}>
                          <Check size={14} className="mr-1" /> Onayla
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 bg-red-50 text-red-700 hover:bg-red-100 border-red-200" onClick={() => handleReject(tx.id)} data-testid={`reject-tx-${tx.id}`}>
                          <X size={14} className="mr-1" /> Reddet
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
